/**
 * Curated persona banks — Capability / Difficulties / Interests / Dislikes / Domain
 * are intentionally DIFFERENT lists (no shared “same 3 fields” options).
 */

export const CAPABILITY_GROUPS = [
  {
    label: "Soft strengths",
    options: [
      "Communication", "Presentation", "Leadership", "Teamwork", "Empathy",
      "Time management", "Adaptability", "Creativity", "Critical thinking",
      "Aptitude", "Logical thinking", "Analytical thinking", "Writing",
      "Mentoring juniors", "Ownership / accountability",
    ],
  },
  {
    label: "Tech strengths",
    options: [
      "Python", "JavaScript", "Java", "C / C++", "SQL", "React", "Node.js",
      "HTML / CSS", "Git", "Debugging", "DSA basics", "API basics",
      "Prompt engineering", "Using ChatGPT / Copilot well", "Excel / Sheets",
      "UI design eye", "Documentation",
    ],
  },
  {
    label: "Work style",
    options: [
      "Fast learner", "Detail-oriented", "Self-starter", "Calm under pressure",
      "Asks good questions", "Explains simply", "Hands-on builder",
      "Research-first", "Pair-programming friendly",
    ],
  },
];

export const DIFFICULTY_GROUPS = [
  {
    label: "Learning struggles",
    options: [
      "Weak in DSA", "Weak in math / aptitude", "Slow at coding syntax",
      "Hard to debug", "Forgets concepts quickly", "Struggles with English writing",
      "Public speaking anxiety", "Hard to start tasks", "Procrastination",
      "Gets stuck alone", "Overwhelmed by big problems", "Weak in system design",
      "Hard to read long docs", "Exam anxiety", "Time pressure in contests",
    ],
  },
  {
    label: "Work / focus struggles",
    options: [
      "Easily distracted", "Hard to estimate time", "Perfectionism slows delivery",
      "Avoids asking for help", "Inconsistent daily practice", "Sleep / energy dips",
      "Group conflicts", "Hard to prioritize",
    ],
  },
];

export const INTEREST_GROUPS = [
  {
    label: "Sports & fitness",
    options: [
      "Cricket", "Football", "Basketball", "Badminton", "Volleyball", "Tennis",
      "Athletics / running", "Gym / fitness", "Yoga", "Swimming", "Kabaddi",
      "Table tennis", "Cycling", "Martial arts", "Chess",
    ],
  },
  {
    label: "Arts & performance",
    options: [
      "Dance", "Classical dance", "Western dance", "Music", "Singing",
      "Guitar / instruments", "DJ / music production", "Theatre / drama",
      "Drawing / sketching", "Painting", "Photography", "Videography",
      "Content creation", "Podcasting",
    ],
  },
  {
    label: "Hobbies & lifestyle",
    options: [
      "Reading novels", "Anime / manga", "Gaming", "Travel", "Cooking",
      "Baking", "Gardening", "Volunteering", "Debate", "Quiz clubs",
      "Blogging", "Fashion / styling", "Movies / short films", "Board games",
      "Astronomy", "DIY / making things",
    ],
  },
  {
    label: "Career / learning interests",
    options: [
      "Building side projects", "Hackathons", "Open source", "Startups",
      "Product thinking", "UI/UX", "Data stories", "Teaching peers",
      "AI tools exploration", "Automation / no-code", "Competitive programming",
      "Cybersecurity curious", "Robotics / IoT tinkering",
    ],
  },
];

export const DISLIKE_GROUPS = [
  {
    label: "Activities they avoid",
    options: [
      "Long lectures", "Rote memorization", "Public speaking", "Group presentations",
      "Competitive coding contests", "Heavy theory without practice", "Night-long coding",
      "Strict timelines", "Repetitive drills", "Reading dense papers",
      "Cold-calling in class", "Solo projects with no feedback",
    ],
  },
  {
    label: "Topic / vibe dislikes",
    options: [
      "Pure math-heavy work", "Hardware soldering", "Sales-y tasks",
      "Overly formal writing", "Unclear requirements", "Busywork / filler tasks",
      "Forced icebreakers", "Gaming as a task", "Dance / stage performance",
    ],
  },
];

export const DOMAIN_GROUPS = [
  {
    label: "Industry / product",
    options: [
      "FinTech", "EdTech", "HealthTech", "E-commerce", "SaaS / B2B",
      "AgriTech", "Climate / sustainability", "Media / entertainment",
      "Logistics", "HR Tech", "LegalTech", "Travel Tech", "Social impact",
    ],
  },
  {
    label: "Tech focus",
    options: [
      "Web frontend", "Backend APIs", "Full-stack", "Mobile apps",
      "AI / ML apps", "Data analytics", "Cloud / DevOps", "Cybersecurity",
      "IoT / embedded", "Game development", "AR / VR", "Blockchain curious",
      "Automation / agents", "QA / testing",
    ],
  },
  {
    label: "Role direction",
    options: [
      "Software engineer", "Product manager path", "UI/UX designer path",
      "Data analyst path", "DevRel / community", "Research intern path",
      "Founder / builder path", "Teaching / mentoring path",
    ],
  },
];

/** Flat lists for Autocomplete */
export function flattenGroups(groups) {
  return (groups || []).flatMap((g) => g.options || []);
}

export const CAPABILITY_SUGGESTIONS = flattenGroups(CAPABILITY_GROUPS);
export const DIFFICULTY_SUGGESTIONS = flattenGroups(DIFFICULTY_GROUPS);
export const INTEREST_SUGGESTIONS = flattenGroups(INTEREST_GROUPS);
export const DISLIKE_SUGGESTIONS = flattenGroups(DISLIKE_GROUPS);
export const DOMAIN_SUGGESTIONS = flattenGroups(DOMAIN_GROUPS);

/** Most-picked / campus-popular defaults (Google-style “trending”) */
export const POPULAR_BY_KIND = {
  capability: [
    "Communication", "Python", "JavaScript", "Logical thinking", "Aptitude",
    "React", "Teamwork", "Fast learner", "DSA basics", "Prompt engineering",
    "Presentation", "Git", "Debugging", "Leadership", "SQL",
  ],
  difficulties: [
    "Tamil medium", "Difficulty in English", "Weak in DSA", "Public speaking anxiety",
    "Procrastination", "Hard to debug", "Exam anxiety", "Easily distracted",
    "Weak in system design", "Hard to start tasks", "Gets stuck alone",
    "Forgets concepts quickly", "Time pressure in contests",
  ],
  interest: [
    "Cricket", "Dance", "Music", "Football", "Gaming", "Singing", "Photography",
    "Badminton", "Travel", "Content creation", "Gym / fitness", "Chess",
    "Building side projects", "Anime / manga", "Hackathons", "Cooking",
  ],
  dislikes: [
    "Long videos / YouTube lectures", "Long lectures", "Rote memorization", "Public speaking",
    "Repetitive drills", "Heavy theory without practice", "Busywork / filler tasks",
    "Night-long coding", "Competitive coding contests", "Strict timelines",
  ],
  domain: [
    "Full-stack", "AI / ML apps", "Web frontend", "FinTech", "Software engineer",
    "Mobile apps", "EdTech", "Data analytics", "Cloud / DevOps", "Product manager path",
    "Automation / agents", "SaaS / B2B",
  ],
};

/** Aliases so “foot”, “crick”, “py” surface the right popular matches */
export const SEARCH_ALIASES = {
  foot: ["Football"],
  football: ["Football"],
  soccer: ["Football"],
  crick: ["Cricket"],
  cricket: ["Cricket"],
  bask: ["Basketball"],
  basketball: ["Basketball"],
  badm: ["Badminton"],
  badminton: ["Badminton"],
  volleyball: ["Volleyball"],
  tennis: ["Tennis"],
  swim: ["Swimming"],
  dance: ["Dance", "Classical dance", "Western dance"],
  music: ["Music", "Singing", "Guitar / instruments"],
  sing: ["Singing", "Music"],
  song: ["Singing", "Music"],
  guitar: ["Guitar / instruments", "Music"],
  gym: ["Gym / fitness"],
  fitness: ["Gym / fitness"],
  yoga: ["Yoga"],
  photo: ["Photography"],
  camera: ["Photography", "Videography"],
  game: ["Gaming", "Game development"],
  gaming: ["Gaming"],
  anime: ["Anime / manga"],
  cook: ["Cooking", "Baking"],
  travel: ["Travel"],
  code: ["Python", "JavaScript", "Building side projects", "Competitive programming"],
  coding: ["Python", "JavaScript", "Building side projects"],
  python: ["Python"],
  py: ["Python"],
  js: ["JavaScript"],
  javascript: ["JavaScript"],
  java: ["Java"],
  react: ["React", "Web frontend"],
  node: ["Node.js", "Full-stack"],
  sql: ["SQL", "Data analytics"],
  dsa: ["DSA basics", "Weak in DSA", "Competitive programming"],
  algo: ["DSA basics", "Logical thinking", "Competitive programming"],
  speak: ["Public speaking", "Public speaking anxiety", "Presentation", "Communication"],
  talk: ["Communication", "Presentation", "Public speaking"],
  shy: ["Public speaking anxiety", "Avoids asking for help"],
  focus: ["Easily distracted", "Hard to prioritize", "Inconsistent daily practice"],
  lazy: ["Procrastination", "Hard to start tasks"],
  ai: ["AI / ML apps", "AI tools exploration", "Automation / agents", "Prompt engineering"],
  ml: ["AI / ML apps", "Data analytics"],
  fintech: ["FinTech"],
  web: ["Web frontend", "Full-stack"],
  mobile: ["Mobile apps"],
  cloud: ["Cloud / DevOps"],
  design: ["UI/UX", "UI design eye", "UI/UX designer path"],
  lead: ["Leadership", "Mentoring juniors"],
  team: ["Teamwork", "Pair-programming friendly"],
  math: ["Weak in math / aptitude", "Aptitude", "Pure math-heavy work"],
  write: ["Writing", "Struggles with English writing", "Overly formal writing"],
  hack: ["Hackathons", "Building side projects"],
  startup: ["Startups", "Product thinking"],
};

function popularityRank(kind, label) {
  const popular = POPULAR_BY_KIND[kind] || [];
  const idx = popular.findIndex((p) => p.toLowerCase() === String(label).toLowerCase());
  if (idx === -1) return 0;
  return popular.length - idx; // higher = more popular
}

/** Cheap fuzzy similarity 0–1 (shared chars + token overlap + prefix). */
function fuzzyScore(query, text) {
  const q = String(query || "").toLowerCase().trim();
  const t = String(text || "").toLowerCase().trim();
  if (!q || !t) return 0;
  if (t === q) return 1;
  if (t.startsWith(q) || q.startsWith(t)) return 0.92;
  if (t.includes(q) || q.includes(t)) return 0.75;

  const qTokens = q.split(/[^a-z0-9]+/).filter((w) => w.length >= 2);
  const tTokens = t.split(/[^a-z0-9]+/).filter((w) => w.length >= 2);
  let tokenHits = 0;
  qTokens.forEach((qw) => {
    if (tTokens.some((tw) => tw.startsWith(qw) || qw.startsWith(tw) || tw.includes(qw))) tokenHits += 1;
  });
  const tokenScore = qTokens.length ? tokenHits / qTokens.length : 0;

  // character bigram overlap
  const bigrams = (s) => {
    const out = new Set();
    for (let i = 0; i < s.length - 1; i++) out.add(s.slice(i, i + 2));
    return out;
  };
  const qb = bigrams(q.replace(/\s+/g, ""));
  const tb = bigrams(t.replace(/\s+/g, ""));
  let shared = 0;
  qb.forEach((b) => { if (tb.has(b)) shared += 1; });
  const biScore = qb.size ? shared / qb.size : 0;

  // tiny edit distance boost for short single words
  let editBoost = 0;
  if (q.length <= 10 && !q.includes(" ")) {
    const dist = levenshtein(q, t.slice(0, Math.max(q.length + 2, t.split(/\s/)[0]?.length || q.length)));
    if (dist <= 2) editBoost = 0.55 - dist * 0.15;
  }

  return Math.max(tokenScore * 0.7 + biScore * 0.3, editBoost, biScore * 0.5);
}

function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

/**
 * Google-like ranking: exact / prefix / alias / fuzzy similar.
 * Always returns related options when the query is novel (out of bank).
 */
export function rankSearchResults(kind, query, pool, { selected = [], limit = 24 } = {}) {
  const q = String(query || "").trim().toLowerCase();
  const selectedSet = new Set((selected || []).map((s) => String(s).toLowerCase()));
  const aliasHits = new Set();
  if (q) {
    Object.entries(SEARCH_ALIASES).forEach(([key, vals]) => {
      if (
        key === q ||
        key.startsWith(q) ||
        q.startsWith(key) ||
        key.includes(q) ||
        q.includes(key) ||
        fuzzyScore(q, key) >= 0.4
      ) {
        vals.forEach((v) => aliasHits.add(v.toLowerCase()));
      }
    });
  }

  const scored = [];
  for (const label of pool || []) {
    if (selectedSet.has(String(label).toLowerCase())) continue;
    const t = String(label).toLowerCase();
    let score = 0;
    if (!q) {
      score = 10 + popularityRank(kind, label);
    } else {
      if (t === q) score = 1000;
      else if (t.startsWith(q)) score = 800;
      else if (t.split(/[\s/|&-]+/).some((w) => w.startsWith(q))) score = 650;
      else if (t.includes(q) || q.includes(t)) score = 400;
      else if (aliasHits.has(t)) score = 550;
      else {
        const soft = fuzzyScore(q, t);
        if (soft >= 0.18) score = Math.round(100 + soft * 300);
        else continue;
      }
      score += popularityRank(kind, label) * 3;
    }
    scored.push({
      label,
      score,
      group: !q ? "Suggestions" : score >= 400 ? "Best matches" : "Similar",
    });
  }

  scored.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));

  // Any typed query: never leave the menu empty — always add similar + popular
  if (q) {
    const have = new Set(scored.map((s) => s.label.toLowerCase()));
    (POPULAR_BY_KIND[kind] || []).forEach((label) => {
      if (selectedSet.has(label.toLowerCase()) || have.has(label.toLowerCase())) return;
      const soft = fuzzyScore(q, label);
      scored.push({
        label,
        score: Math.round(35 + soft * 120),
        group: soft >= 0.2 ? "Similar" : "You may also like",
      });
      have.add(label.toLowerCase());
    });
    for (const label of pool || []) {
      if (scored.length >= limit) break;
      if (selectedSet.has(label.toLowerCase()) || have.has(label.toLowerCase())) continue;
      const soft = fuzzyScore(q, label);
      if (soft < 0.1) continue;
      scored.push({ label, score: Math.round(25 + soft * 90), group: "Similar" });
      have.add(label.toLowerCase());
    }
    scored.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  }

  return scored.slice(0, limit);
}

export const PROFILE_KIND_META = {
  capability: {
    label: "Strengths & skills",
    placeholder: "Search like Google — try “python”, “speak”, “lead”…",
    hint: "What this student is already good at — soft + tech + work style.",
    tone: "teal",
    groups: CAPABILITY_GROUPS,
  },
  difficulties: {
    label: "Difficulties / weak spots",
    placeholder: "Search — try “dsa”, “speak”, “focus”…",
    hint: "Honest blockers — used to pace tasks and support, not to judge.",
    tone: "slate",
    groups: DIFFICULTY_GROUPS,
  },
  interest: {
    label: "Likes & activities",
    placeholder: "Search — try “cricket”, “dance”, “music”, “gym”…",
    hint: "Personal likes — sports, arts, hobbies + light career interests for Mini Build.",
    tone: "coral",
    groups: INTEREST_GROUPS,
  },
  dislikes: {
    label: "Dislikes / avoid",
    placeholder: "Search — try “lecture”, “theory”, “contest”…",
    hint: "Things to avoid or keep short in their schedule.",
    tone: "coral",
    groups: DISLIKE_GROUPS,
  },
  domain: {
    label: "Domain / direction",
    placeholder: "Search — try “fintech”, “ai”, “web”, “fullstack”…",
    hint: "Where they want to grow — industry, tech focus, or role path (not hobbies).",
    tone: "amber",
    groups: DOMAIN_GROUPS,
  },
};
